/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getLanguage} from '@ion/core/src/locale/locale';
import {LanguageIds, SisenseState} from '@ion/core/src/services/SisenseUtils';
import {getSisenseTheme} from '@ion/dls/src/theme/resellers/Resellers';
import {setSisenseDefaultDashboard} from '../../pages/reports/state';
import {sisenseDefaultDashboardSelector} from '../../store/selectors/reports';
import useSisenseSelector from './useSisenseSelector';
import {getDefaultURL, getOIDURL, getReportsURL} from './utils';

interface FrameOptions {
  showLeftPanel?: boolean;
  features?: string[];
  oid?: string;
};

/**
 * The useDashboardIFramePath hook is used to create the Sisense dashboard path within an application.
 * It is designed to handle creating path, managing default landing dashboard, and handling removing
 * landing dashboard state, particularly in the context of a report manager.
 *
 * ```typescript
 *
 *  import {useDashboardIFramePath} from '@ion/applications/src/sisense/hooks';
 *
 *  export const Component = ({isReportManager}) => {
 *    const path  = useDashboardIFramePath(isReportManager);
 *
 *    return <iframe url={path} options={options} />;
 *  };
 *
 * ```
 *
 * @param isReportManager A flag to indicate whether the user is a report manager.
 */
export const useDashboardIFramePath = (isReportManager = false, options: FrameOptions = {
  showLeftPanel : true,
  features      : [],
  oid           : null
}) => {
  const dispatch         = useDispatch();
  const defaultDashboard = useSelector(sisenseDefaultDashboardSelector);
  const serverOptions    = useSisenseSelector(SisenseState.ServerOptions);

  const {
    showLeftPanel,
    oid,
    features
  }  = options;

  useEffect(() => {
    if (!isReportManager && defaultDashboard) {
      return () => {
        dispatch(setSisenseDefaultDashboard(null));
      };
    }
  }, [isReportManager, defaultDashboard]);

  const path = useMemo(() => {
    const theme         = getSisenseTheme(serverOptions?.themeId as string);
    const lang          = LanguageIds[getLanguage()];
    const themeParam    = theme ? `&theme=${theme}` : '';
    const featuresQuery = features?.length ? `&features=${features.join(',')}` : '';

    if (oid) {
      return getOIDURL({oid, themeParam, lang, showLeftPanel, featuresQuery});
    }

    if (isReportManager) {
      return getReportsURL(themeParam, lang);
    }

    const route = defaultDashboard ? `dashboards/${defaultDashboard}` : 'home';
    return getDefaultURL({route, themeParam, lang, showLeftPanel, featuresQuery});
  }, [isReportManager, defaultDashboard, serverOptions, options]);

  return path;
};

export default useDashboardIFramePath;
